import React from 'react'
import { graphql, Link, useStaticQuery } from 'gatsby'
import tw, { styled } from 'twin.macro'
import Img from 'gatsby-image'
import PropTypes from 'prop-types'

const Wrapper = styled.div`
	${tw`flex flex-col items-center gap-4 pt-0 pb-8 px-4 bg-white md:gap-8 md:pt-0 md:pb-12 -mt-2 md:-mt-4`}
`

const BannerSection = styled.section`
	${tw`flex flex-col items-center w-full`}
	max-width: 1100px;
`

const Heading = styled.h2`
	${tw`text-black font-black text-center mb-3`}
	font-weight: 800;
	font-size: clamp(1.4rem, 3vw, 2.8rem);
	font-family: Flexo, system-ui, sans-serif !important;
`

const Subheading = styled.p`
	${tw`text-black text-center max-w-4xl mb-6 md:mb-8 font-semibold`}
	font-size: clamp(0.875rem, 2vw, 1rem);
	line-height: 1.6;
`

const RoundedContainer = styled.div`
	border-radius: clamp(16px, 4vw, 45px);
	overflow: hidden;
	position: relative;
`

const BannerOverlay = styled.div`
	${tw`hidden md:flex absolute inset-0 items-end justify-center`}
	padding-bottom: 2rem;
`

const CommercialBannerOverlay = styled.div`
	${tw`hidden md:flex absolute inset-0 justify-center`}
	align-items: center;
	padding-top: 13%;
`

const BannerButton = styled(Link)`
	${tw`font-semibold cursor-pointer inline-flex items-center gap-2`}
	background: transparent;
	color: white;
	border: 2px solid white;
	border-radius: 9999px;
	font-size: 0.9rem;
	text-decoration: none;
	padding: 0.5rem 1.5rem;

	&::after {
		content: '→';
	}
`

const MobileBannerButton = styled(Link)`
	${tw`md:hidden font-semibold cursor-pointer inline-flex items-center justify-center gap-2 mt-4`}
	background: transparent;
	color: black;
	border: 2px solid black;
	border-radius: 9999px;
	font-size: 0.9rem;
	text-decoration: none;
	padding: 0.65rem 2.5rem;
	min-height: 44px;
	display: flex;
	margin-left: auto;
	margin-right: auto;

	&::after {
		content: '→';
	}
`

const ExploreBanner = ({ locale }) => {
	const data = useStaticQuery(ExploreBannerQuery)
	const niya = data.NiyaBanner?.nodes[0]
	const skimmi = data.SkimmiBanner?.nodes[0]
	const commercial = data.CommercialBanner?.nodes[0]

	return (
		<Wrapper>
			<BannerSection>
				<Heading>Smart Value</Heading>
				<Subheading>
					Smart, powerful and great value. The Niya range of cordless robotic pool
					cleaners deliver reliable, automated pool cleaning with user friendly
					features for everyday pool care.
				</Subheading>
				<RoundedContainer css={[tw`text-center max-w-screen-xl w-full mx-auto`]}>
					<Img
						{...niya?.backgroundImage}
						loading="lazy"
						css={[tw`w-full mx-auto`]}
					/>
					<BannerOverlay>
						<BannerButton to={'/' + niya?.mainActionUrl}>{niya?.mainActionText}</BannerButton>
					</BannerOverlay>
				</RoundedContainer>
				<MobileBannerButton to={'/' + niya?.mainActionUrl}>{niya?.mainActionText}</MobileBannerButton>
			</BannerSection>

			<BannerSection>
				<Heading>Surface Cleaners</Heading>
				<Subheading>
					Discover the Dolphin Skimmi solar-powered robotic pool skimmer which
					automatically removes leaves, insects and floating debris from your pool
					surface before it sinks.
				</Subheading>
				<RoundedContainer css={[tw`text-center max-w-screen-xl w-full mx-auto`]}>
					<Img
						{...skimmi?.backgroundImage}
						loading="lazy"
						css={[tw`w-full mx-auto`]}
					/>
					<BannerOverlay>
						<BannerButton to={'/' + skimmi?.mainActionUrl}>{skimmi?.mainActionText}</BannerButton>
					</BannerOverlay>
				</RoundedContainer>
				<MobileBannerButton to={'/' + skimmi?.mainActionUrl}>{skimmi?.mainActionText}</MobileBannerButton>
			</BannerSection>

			<BannerSection>
				<Heading>Commercial Robots</Heading>
				<Subheading>
					Trusted by professionals, Dolphin Wave Commercial Pool Cleaners are the
					#1 choice for commercial pools of every size and type
				</Subheading>
				<RoundedContainer css={[tw`text-center max-w-screen-xl w-full mx-auto`]}>
					<Img
						{...commercial?.backgroundImage}
						loading="lazy"
						css={[tw`w-full mx-auto`]}
					/>
					<CommercialBannerOverlay>
						<BannerButton to={'/' + commercial?.mainActionUrl}>{commercial?.mainActionText}</BannerButton>
					</CommercialBannerOverlay>
				</RoundedContainer>
				<MobileBannerButton to={'/' + commercial?.mainActionUrl}>{commercial?.mainActionText}</MobileBannerButton>
			</BannerSection>
		</Wrapper>
	)
}

export const ExploreBannerQuery = graphql`
	query ExploreBannerQuery {
		NiyaBanner: allContentfulBanner(
			filter: { contentful_id: { eq: "2MKZUS2dTl2D49Vdf4SPpY" }, node_locale: { eq: "en-AU" } }
		) {
			nodes {
				backgroundImage {
					title
					alt: description
					fluid(maxWidth: 1920, quality: 80) {
						...GatsbyContentfulFluid_withWebp
					}
				}
				mainActionText
				mainActionUrl
				hook
			}
		}

		SkimmiBanner: allContentfulBanner(
			filter: { contentful_id: { eq: "1M4bDcnnVX8phCsj186oZc" }, node_locale: { eq: "en-AU" } }
		) {
			nodes {
				backgroundImage {
					title
					alt: description
					fluid(maxWidth: 1920, quality: 80) {
						...GatsbyContentfulFluid_withWebp
					}
				}
				mainActionText
				mainActionUrl
				hook
			}
		}

		CommercialBanner: allContentfulBanner(
			filter: { contentful_id: { eq: "6uTX0AmD1lo9ijAb58xk6T" }, node_locale: { eq: "en-AU" } }
		) {
			nodes {
				backgroundImage {
					title
					alt: description
					fluid(maxWidth: 1920, quality: 80) {
						...GatsbyContentfulFluid_withWebp
					}
				}
				mainActionText
				mainActionUrl
				hook
			}
		}
	}
`

ExploreBanner.propTypes = {
	locale: PropTypes.string.isRequired,
}

export default ExploreBanner
